import { Form, Formik, FormikProps } from 'formik';
import { cloneDeep, merge } from 'lodash';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { OnFormChange } from '../../../../../../components/form/form-change';
import { ContentWithFooter } from '../../../../../../components/ui/content-with-footer';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { PickerInput } from '../../../../shared/component/widget/picker-input';
import { WidgetProps } from '../../../../shared/component/widget/widget.types';
import { ApplicationDefaultParameterSetFormikFormValues } from '../../../../shared/hook/application-default-parameter-set.hooks';
import { useFlowServices } from '../../../../shared/hook/flow-service.hooks';
import {
  ApplicationDefaultParameterSetBaseFormProps,
  Flow,
  FlowItemForm,
} from '../../../../shared/model/flow.types';
import {
  FlowService,
  GetYupSchemaForItemContext,
} from '../../../../shared/service/service.types';
import { getParameterValueFromForm } from '../../../../shared/util/flow-parameter.util';
import { BeaconWeldingData } from '../../model/beacon-welding-data';
import { WeldProcessSettingsWithRecommendations } from '../flow-item/weld-process-settings-with-recommendations';
import { WeldApplicationSettingsTags } from './weld-application-settings-tags';

export type WeldParameterSetBaseFormValidationBuilder = (
  dataSchema: yup.AnySchema<unknown, unknown, unknown> | undefined
) => yup.AnyObjectSchema;

interface WeldApplicationSetBaseFormTabbedProps<T>
  extends ApplicationDefaultParameterSetBaseFormProps<T> {
  onSubmit: (form: T, recommendationId?: string) => Promise<void>;
}

export type WeldApplicationSettingsFormValues = Pick<
  ApplicationDefaultParameterSetFormikFormValues,
  'tags' | 'data'
>;

function WeldApplicationSettingsBaseFormTabbed(
  props: WeldApplicationSetBaseFormTabbedProps<WeldApplicationSettingsFormValues>
): ReactElement {
  const {
    paramsReadOnly,
    tagsReadOnly,
    excludeTags,
    system,
    footer,
    formValues,
    onSubmit,
    preForm,
    postForm,
    buildValidationSchema,
  } = props;

  const formikRef =
    useRef<FormikProps<WeldApplicationSettingsFormValues> | null>(null);

  const [recommendationId, setRecommendationId] = useState<
    string | undefined
  >();

  const [recommendationLoading, setRecommendationLoading] = useState(false);

  const setInnerRef = useCallback(
    (instance: FormikProps<WeldApplicationSettingsFormValues> | null) => {
      formikRef.current = instance;
    },
    []
  );

  const [dataSchema, setDataSchema] = useState<yup.AnySchema>();
  const [validationSchemaContext, setValidationSchemaContext] =
    useState<GetYupSchemaForItemContext>();

  const { flow, flowService, flowItemService } =
    useFlowServices<BeaconWeldingData.FlowConfig>();

  const validationSchema = useMemo(() => {
    return buildValidationSchema(dataSchema);
  }, [buildValidationSchema, dataSchema]);

  const setParameterValidationSchema = useCallback((schema?: yup.AnySchema) => {
    requestAnimationFrame(() => {
      setDataSchema(schema);

      setTimeout(() => {
        formikRef.current?.validateForm();
      }, 1);
    });
  }, []);

  const parameterOptions = merge(
    cloneDeep(BeaconWeldingData.DefaultWeldParameterSetParameterFormOptions),
    {
      forceExcludeRefs: [
        ...(BeaconWeldingData.DefaultWeldParameterSetParameterFormOptions
          .forceExcludeRefs ?? []),
        BeaconWeldingData.FlowItemParameterRef.PROCESS,
      ],
    } as Partial<BeaconWeldingData.GetParameterMetadataFromFormOptions>
  );

  const recalculateParameterValues = useCallback(
    (form: FlowItemForm) => {
      const item = flowItemService.convertFormToFlowItem(form, system);

      const recalculatedItems = flowItemService.recalculateParameterValues(
        flow.config,
        [item],
        {
          isDefaultParameterSet: true,
        }
      );
      const recalculatedForm = flowItemService.convertFlowItemToForm(
        recalculatedItems[0],
        system
      );
      return recalculatedForm;
    },
    [flow.config, flowItemService, system]
  );

  const innerOnSubmit = (form: WeldApplicationSettingsFormValues) => {
    onSubmit(form, recommendationId);
  };

  /**
   * Form change callback that recomputes the validation schema context.
   * Libraries have a portion of the settings that drive parameter validation
   * in the form itself so we need to update the validation schema context based
   * on this forms values
   */
  const onFormChange = useCallback(
    (form: WeldApplicationSettingsFormValues) => {
      const context = buildValidationSchemaContext({
        flowService,
        flow,
        form,
      });

      setValidationSchemaContext(context);
    },
    [flowService, flow]
  );

  return (
    <Formik
      initialValues={formValues}
      onSubmit={innerOnSubmit}
      enableReinitialize={true}
      validationSchema={validationSchema}
      innerRef={setInnerRef}
    >
      {(formikProps) => {
        const { values } = formikProps;
        const internalFooter = footer({
          formikProps,
          disabled: recommendationLoading,
        });
        const internalPreForm =
          typeof preForm === 'function' ? preForm({ formikProps }) : null;
        const internalPostForm =
          typeof postForm === 'function' ? postForm({ formikProps }) : null;
        const process =
          getParameterValueFromForm<BeaconWeldingData.WeldProcess>(
            values.data.parameters,
            BeaconWeldingData.FlowItemParameterRef.PROCESS
          );

        return (
          <ContentWithFooter
            footer={internalFooter}
            forceHeightForFooter={true}
          >
            <Form>
              <OnFormChange onFormChange={onFormChange} />
              {internalPreForm}
              <ProcessInput readOnly={paramsReadOnly} />
              <WeldApplicationSettingsTags
                process={process}
                weldParameterSetFormTags={values.tags}
                readOnly={tagsReadOnly}
                excludeTags={[
                  ...(excludeTags ?? []),
                  BeaconWeldingData.ApplicationDefaultParameterSetTag.POSITION,
                ]}
              />
              <div style={{ height: 20 }} />
              <WeldProcessSettingsWithRecommendations
                system={system}
                readOnly={paramsReadOnly}
                formikValuePath="data"
                setValidationSchema={setParameterValidationSchema}
                parameterListPrefix="data.parameters"
                parameterOptions={parameterOptions}
                validationSchemaContext={validationSchemaContext}
                recalculateParameterValues={recalculateParameterValues}
                onRecommendSettings={setRecommendationId}
                onRecommendSettingsLoading={setRecommendationLoading}
              />
              {internalPostForm}
            </Form>
          </ContentWithFooter>
        );
      }}
    </Formik>
  );
}

const buildValidationSchemaContext = ({
  flowService,
  flow,
  form,
}: {
  flowService: FlowService<BeaconWeldingData.FlowConfig>;
  flow: Flow<BeaconWeldingData.FlowConfig>;
  form: WeldApplicationSettingsFormValues;
}): GetYupSchemaForItemContext => {
  const flowWithMappedTags =
    flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
      flow,
      form.tags
    );

  // map parameter form to context since this form changes both config values
  // on the value and sets default parameter values
  return {
    flow: flowWithMappedTags,
    form: form.data,
  };
};

interface ProcessInputProps {
  readOnly?: boolean;
}

const ProcessInput = (props: ProcessInputProps): ReactElement => {
  const { readOnly } = props;
  const { flowItemService } = useFlowServices<BeaconWeldingData.FlowConfig>();

  const processMeta = flowItemService.getParameterMetadata(
    BeaconWeldingData.FlowItemRef.WELD,
    BeaconWeldingData.FlowItemParameterRef.PROCESS
  );

  const sharedProps: WidgetProps = {
    label: getLocalizedText(processMeta?.name),
    ionItemProps: { lines: 'full' },
  };

  const prefix = 'data.parameters';
  const name = `${prefix}.${processMeta?.ref}.value`;

  return (
    <PickerInput
      name={name}
      disabled={readOnly}
      options={processMeta?.ui?.widgetOptions}
      placeholder={getLocalizedText('welding:make_selection')}
      {...sharedProps}
    />
  );
};

export { WeldApplicationSettingsBaseFormTabbed };
